import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, R_EVENTO, R_STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { sortReservas } from "@/pages/tenant/ReservasPage";
import { fmtBRL, fmtDataBR } from "@/components/ReservaFormDialog";
import { Printer } from "lucide-react";

const COL_LABEL = {
  numero: "Nº", area_name: "Espaço", data: "Data", inicio: "Horário",
  solicitante_nome: "Solicitante", eql_label: "Unidade", evento_tipo: "Evento",
  status: "Status", valor: "Taxa",
};

export default function ReservasPrintPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [empreendimento, setEmpreendimento] = useState(null);

  const sortKey = params.get("sort") || "data";
  const sortDir = params.get("dir") || "desc";
  const dateFrom = params.get("date_from") || "";
  const dateTo = params.get("date_to") || "";

  useEffect(() => {
    const qp = {};
    for (const k of ["q", "area_id", "status", "date_from", "date_to"]) {
      const v = params.get(k);
      if (v) qp[k] = v;
    }
    api.get("/tenant/reservas", { params: qp }).then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/tenant/brave/empreendimento")
      .then(({ data }) => setEmpreendimento(data?.razao_social || data?.apelido || null))
      .catch(() => setEmpreendimento(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!items) return <div className="min-h-screen bg-white flex items-center justify-center"><Spinner /></div>;

  const sorted = sortReservas(items, sortKey, sortDir);
  const impressoEm = new Date().toLocaleString("pt-BR");
  const periodo = dateFrom && dateTo && dateFrom === dateTo
    ? fmtDataBR(dateFrom)
    : dateFrom && dateTo
      ? `${fmtDataBR(dateFrom)} a ${fmtDataBR(dateTo)}`
      : dateFrom
        ? `a partir de ${fmtDataBR(dateFrom)}`
        : dateTo ? `até ${fmtDataBR(dateTo)}` : "Todos os períodos";

  return (
    <div className="dark min-h-screen bg-white text-zinc-900 p-6 sm:p-10" data-testid="reservas-print-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-6 print:hidden">
          <p className="text-sm text-zinc-500">Visualização de impressão — use o botão para imprimir ou salvar em PDF.</p>
          <Button data-testid="print-pdf-button" onClick={() => window.print()}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            <Printer className="h-4 w-4 mr-1.5" /> Imprimir PDF
          </Button>
        </div>

        <header className="border-b-2 border-zinc-900 pb-4 mb-4" data-testid="print-header">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Help Desk — Relatório de Reservas</p>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h1 className="text-xl font-semibold" data-testid="print-empreendimento">
              {empreendimento || user?.tenant_name || "—"}
            </h1>
            <div className="text-right text-xs text-zinc-600 space-y-0.5">
              <p data-testid="print-operador">Operador: <span className="font-medium text-zinc-900">{user?.name}</span></p>
              <p data-testid="print-datahora">Impresso em: {impressoEm}</p>
              <p data-testid="print-periodo">Período: <span className="font-medium text-zinc-900">{periodo}</span></p>
              <p data-testid="print-ordenacao">Ordenação: {COL_LABEL[sortKey] || "Data"} ({sortDir === "asc" ? "crescente" : "decrescente"})</p>
            </div>
          </div>
        </header>

        {sorted.length === 0 ? (
          <p className="text-sm text-zinc-500 py-8 text-center" data-testid="print-empty">Nenhuma reserva no período/filtros selecionados.</p>
        ) : (
          <table className="w-full text-xs border-collapse" data-testid="print-table">
            <thead>
              <tr className="bg-zinc-100">
                {["Nº", "Espaço", "Data", "Horário", "Solicitante", "Unidade", "Evento", "Status", "Taxa"].map((h) => (
                  <th key={h} className="border border-zinc-300 px-2 py-1.5 text-left font-semibold text-zinc-700">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} data-testid={`print-row-${r.id}`}>
                  <td className="border border-zinc-300 px-2 py-1.5 font-mono">#{String(r.numero).padStart(4, "0")}</td>
                  <td className="border border-zinc-300 px-2 py-1.5">{r.area_name}</td>
                  <td className="border border-zinc-300 px-2 py-1.5 whitespace-nowrap">{fmtDataBR(r.data)}</td>
                  <td className="border border-zinc-300 px-2 py-1.5 whitespace-nowrap">{r.inicio}–{r.fim}</td>
                  <td className="border border-zinc-300 px-2 py-1.5">{r.solicitante_nome}</td>
                  <td className="border border-zinc-300 px-2 py-1.5">{r.eql_label ? r.eql_label.split(" — ").pop() : "—"}</td>
                  <td className="border border-zinc-300 px-2 py-1.5">{R_EVENTO[r.evento_tipo] || r.evento_tipo}</td>
                  <td className="border border-zinc-300 px-2 py-1.5">{R_STATUS[r.status] || r.status}</td>
                  <td className="border border-zinc-300 px-2 py-1.5 whitespace-nowrap">{fmtBRL(r.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <footer className="mt-4 pt-3 border-t border-zinc-300 flex items-center justify-between text-xs text-zinc-600">
          <span data-testid="print-total">{sorted.length} reserva(s) listada(s)</span>
          <span>Gerado por Help Desk</span>
        </footer>
      </div>
    </div>
  );
}
